import { atomWithDebounce } from '@rematter/ui-web';
import type { Atom } from 'jotai';
import { atom } from 'jotai';
import uniq from 'lodash/uniq';

import { createFacilityMutationAtom } from '../_graphql/mutationCreateFacility';
import { createFacilityGroupMutationAtom } from '../_graphql/mutationCreateFacilityGroup';
import { deleteFacilityMutationAtom } from '../_graphql/mutationDeleteFacility';
import { deleteFacilityGroupMutationAtom } from '../_graphql/mutationDeleteFacilityGroup';
import { updateFacilityMutationAtom } from '../_graphql/mutationUpdateFacility';
import { updateFacilityGroupMutationAtom } from '../_graphql/mutationUpdateFacilityGroup';
import { queryFacilityGroupAtom } from '../_graphql/queryFacilityGroup';
import { queryFacilityGroupsAtom } from '../_graphql/queryFacilityGroups';
import type { LocationRecord } from '../_graphql/queryLocationList';
import { queryLocationListAtom } from '../_graphql/queryLocationList';

import { settingsRouteParamAtom } from './settingsModule.state';

import type { QueryAtomReadValue } from '~/shared/providers/api/createQueryAtomFactory';

export const locationTabs = [
  'location-info',
  'preferences',
  'document-templates',
] as const;

export const $searchTerm = atomWithDebounce('', 300);
export const $locationTypeFilter = atom('all');
export const $isLocationCreationDialogOpen = atom(false);
export const $locationTab =
  atom<(typeof locationTabs)[number]>('location-info');

export const $rawLocationList = queryLocationListAtom(() => ({
  variables: {
    searchTerm: '',
  },
}));

export const $locationList: Atom<QueryAtomReadValue<LocationRecord[]>> = atom(
  (get) => {
    const rawLocationList = get($rawLocationList);
    const locationTypeFilter = get($locationTypeFilter);
    const searchTerms = get($searchTerm.debouncedValue)
      .toLowerCase()
      .split(/\s+/);

    const data = (rawLocationList.data ?? []).filter((location) => {
      const normalizedSearchable = [
        location.name,
        location.type,
        location.address?.streetAddress,
        location.address?.city,
        location.address?.state,
        location.address?.postalCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return (
        (locationTypeFilter === 'all' ||
          locationTypeFilter === location.type) &&
        searchTerms.every((term) => normalizedSearchable.includes(term))
      );
    });

    return {
      ...rawLocationList,
      data,
    };
  },
);

export const $locationTypeList = atom((get) =>
  uniq(get($rawLocationList).data?.map((location) => location.type)),
);

export const $facilityId = settingsRouteParamAtom(
  [
    '/locations/facilities/:facilityId/info',
    '/locations/facilities/:facilityId/preferences',
    '/locations/facilities/:facilityId/document-templates',
  ],
  'facilityId',
);

export const $currentLocation = atom((get) => {
  const allLocations = get($rawLocationList);
  const facilityId = get($facilityId);

  if (!facilityId) return undefined;

  return allLocations.data?.find((location) => location.id === facilityId);
});

// 🟠🟠🟠 ACTION -> CREATE FACILITY
export const $createFacility = createFacilityMutationAtom({
  onSuccess: (get) => {
    get($rawLocationList).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> DELETE FACILITY
export const $deleteFacility = deleteFacilityMutationAtom({
  onSuccess: (get) => {
    // Refetch location list after successful delete
    get($rawLocationList).refetch?.();
  },
});

// Facility Groups State
export const $groupSearchTerm = atomWithDebounce('', 300);
export const $isAddFacilityGroupDialogOpen = atom(false);

export const $rawFacilityGroupsList = queryFacilityGroupsAtom((get) => ({
  variables: {
    searchTerm: get($groupSearchTerm.debouncedValue) || undefined,
  },
}));

export const $facilityGroupsList = atom((get) => {
  const rawGroupsList = get($rawFacilityGroupsList);
  return rawGroupsList;
});

// 🟠🟠🟠 ACTION -> CREATE FACILITY GROUP
export const $createFacilityGroup = createFacilityGroupMutationAtom({
  onSuccess: (get, set) => {
    get($rawFacilityGroupsList).refetch?.();
    set($isAddFacilityGroupDialogOpen, false);
  },
});

// 🟠🟠🟠 ACTION -> DELETE FACILITY GROUP
export const $deleteFacilityGroup = deleteFacilityGroupMutationAtom({
  onSuccess: (get, _set) => {
    get($rawFacilityGroupsList).refetch?.();
  },
});

// Facility Group Details State
export const locationGroupTabs = [
  'group-info',
  'preferences',
  'document-templates',
] as const;

export const $locationGroupTab =
  atom<(typeof locationGroupTabs)[number]>('group-info');

export const $groupId = settingsRouteParamAtom(
  ['/locations/groups/:groupId'],
  'groupId',
);

export const $currentFacilityGroup = queryFacilityGroupAtom((get) => {
  const groupId = get($groupId);
  if (!groupId) {
    return {
      variables: { id: '' },
      skip: true,
    };
  }

  return {
    variables: { id: groupId },
  };
});

// 🟠🟠🟠 ACTION -> UPDATE FACILITY GROUP
export const $updateFacilityGroup = updateFacilityGroupMutationAtom({
  onSuccess: (get) => {
    get($currentFacilityGroup).refetch?.();
    get($rawFacilityGroupsList).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> UPDATE FACILITY (for group assignment)
export const $updateFacility = updateFacilityMutationAtom({
  onSuccess: (get) => {
    get($rawLocationList).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> SAVE LOCATION GROUP (name + facility assignments)
export const $saveLocationGroup = atom(
  null,
  async (
    get,
    set,
    params: {
      originalFacilityIds: string[];
      form: {
        name: string;
        facilityIds: string[];
        defaultFacilityId?: string | null;
      };
    },
  ) => {
    const currentFacilityGroup = get($currentFacilityGroup);
    if (!currentFacilityGroup?.data?.id) return;

    const groupId = currentFacilityGroup.data.id;
    const { originalFacilityIds, form } = params;

    await set($updateFacilityGroup, {
      id: groupId,
      name: form.name,
      facilityIds: form.facilityIds,
      defaultFacilityId: form.defaultFacilityId,
    });

    // Find facilities to remove (in original but not in new)
    const facilitiesToRemove = originalFacilityIds.filter(
      (id) => !form.facilityIds.includes(id),
    );

    // Find facilities to add (in new but not in original)
    const facilitiesToAdd = form.facilityIds.filter(
      (id) => !originalFacilityIds.includes(id),
    );

    // Update facility assignments in parallel
    await Promise.all([
      ...facilitiesToRemove.map((facilityId) =>
        set($updateFacility, { id: facilityId, facilityGroupId: null }),
      ),
      ...facilitiesToAdd.map((facilityId) =>
        set($updateFacility, { id: facilityId, facilityGroupId: groupId }),
      ),
    ]);

    // Refetch to update the UI
    await currentFacilityGroup.refetch?.();
    await get($rawLocationList).refetch?.();
  },
);
